import type { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { RuleEffect } from '@jsonforms/core';
import {
  type CustomAttributeData,
  customAttributeDataSchema,
  type JSONObject,
  jsonValueSchema,
} from '@risksmart-app/domain/src/types/common.types';
import { z } from 'zod';

import {
  type CustomSchema,
  type CustomUISchema,
  FieldOptionType,
} from './types';
import { validator } from './validator';

const jsonObjectSchema: z.ZodType<JSONObject> = z.record(
  z.string(),
  jsonValueSchema
);

const jsonSchemaTypeSchema = z.enum([
  'array',
  'boolean',
  'number',
  'object',
  'string',
]);

const fieldOptionSchema = z.object({
  const: z.string(),
  title: z.string(),
});

const propertyFilterTokenSchema = z.object({
  propertyKey: z.string().optional(),
  operator: z.string(),
  value: jsonValueSchema,
});

const propertyFilterTokenGroupSchema: z.ZodType = z.lazy(() =>
  z.object({
    operation: z.enum(['and', 'or']),
    tokens: z.array(
      z.union([propertyFilterTokenSchema, propertyFilterTokenGroupSchema])
    ),
  })
);

const propertyFilterQuerySchema = z.object({
  operation: z.enum(['and', 'or']),
  tokens: z.array(propertyFilterTokenSchema),
  tokenGroups: z
    .array(z.union([propertyFilterTokenSchema, propertyFilterTokenGroupSchema]))
    .optional(),
});

const schemaNodeShape = z
  .object({
    type: jsonSchemaTypeSchema.optional(),
    parentId: z.string().optional(),
    isCustomisable: z.boolean().optional(),
    minLength: z.number().int().nonnegative().optional(),
    minItems: z.number().int().nonnegative().optional(),
    isPropertyRequired: z.boolean().optional(),
    allowAttachments: z.boolean().optional(),
    isConditional: z.boolean().optional(),
    uniqueItems: z.boolean().optional(),
    required: z.array(z.string()).optional(),
    conditionalOptions: propertyFilterQuerySchema.optional(),
    oneOf: z.array(fieldOptionSchema).optional(),
    enum: z.array(z.string()).optional(),
    const: z.boolean().optional(),
    properties: z.record(z.string(), z.unknown()).optional(),
    items: z.unknown().optional(),
    allOf: z.array(z.unknown()).optional(),
  })
  .passthrough();

const conditionBranchShape = z.object({
  properties: z.record(z.string(), z.unknown()),
  required: z.array(z.string()),
});

const schemaConditionShape = z.object({
  if: conditionBranchShape,
  then: conditionBranchShape,
});

const isCustomSchemaNode = (
  value: unknown,
  typeRequired: boolean,
  allowedTypes: z.ZodType = jsonSchemaTypeSchema
): boolean => {
  const parsed = schemaNodeShape.safeParse(value);
  if (
    !parsed.success ||
    (typeRequired && parsed.data.type === undefined) ||
    (parsed.data.type !== undefined &&
      !allowedTypes.safeParse(parsed.data.type).success)
  ) {
    return false;
  }

  if (
    parsed.data.properties &&
    !Object.values(parsed.data.properties).every((property) =>
      isCustomSchemaNode(property, true)
    )
  ) {
    return false;
  }

  if (
    parsed.data.items !== undefined &&
    !isCustomSchemaNode(parsed.data.items, false)
  ) {
    return false;
  }

  return (
    parsed.data.allOf?.every((condition) => {
      const parsedCondition = schemaConditionShape.safeParse(condition);
      if (!parsedCondition.success) {
        return false;
      }

      return [
        ...Object.values(parsedCondition.data.if.properties),
        ...Object.values(parsedCondition.data.then.properties),
      ].every((property) => isCustomSchemaNode(property, false));
    }) ?? true
  );
};

const uiSchemaRuleShape = z.object({
  effect: z.nativeEnum(RuleEffect),
  condition: z.object({
    scope: z.string(),
    schema: jsonObjectSchema.refine(
      (schema) => validator.validateSchema(schema) === true
    ),
    failWhenUndefined: z.boolean().optional(),
  }),
});

const uiSchemaElementShape = z
  .object({
    id: z.string(),
    type: z.string(),
    label: z.union([z.string(), z.boolean()]).optional(),
    parentId: z.string().optional(),
    scope: z.string().optional(),
    options: z
      .object({ fieldType: z.nativeEnum(FieldOptionType).optional() })
      .passthrough()
      .optional(),
    rule: uiSchemaRuleShape.optional(),
    elements: z.array(z.unknown()).optional(),
  })
  .passthrough();

const isFormUiSchemaElement = (value: unknown): boolean => {
  const parsed = uiSchemaElementShape.safeParse(value);

  return (
    parsed.success &&
    (parsed.data.elements?.every(isFormUiSchemaElement) ?? true)
  );
};

export const questionnaireFormSchemaContract = z.custom<CustomSchema>(
  (value) =>
    isCustomSchemaNode(value, true) &&
    jsonObjectSchema.safeParse(value).success &&
    validator.validateSchema(value) === true,
  { message: 'Invalid questionnaire form schema' }
);

export const questionnaireFormUiSchemaContract = z.custom<CustomUISchema>(
  (value) => {
    const parsed = z
      .object({
        id: jsonValueSchema.optional(),
        type: z.literal('VerticalLayout'),
        elements: z.array(z.unknown()),
      })
      .safeParse(value);

    return (
      parsed.success &&
      jsonObjectSchema.safeParse(value).success &&
      parsed.data.elements.every(isFormUiSchemaElement)
    );
  },
  { message: 'Invalid questionnaire form UI schema' }
);

const questionnaireRenderingSchemaContract = z.custom<JsonSchema>(
  (value) => {
    const parsed = jsonObjectSchema.safeParse(value);

    return parsed.success && validator.validateSchema(parsed.data) === true;
  },
  { message: 'Invalid questionnaire rendering schema' }
);

const questionnaireRenderingUiSchemaContract = z.custom<UISchemaElement>(
  (value) =>
    jsonObjectSchema.safeParse(value).success &&
    z.object({ type: z.string() }).safeParse(value).success,
  { message: 'Invalid questionnaire rendering UI schema' }
);

const questionnaireCustomAttributeDataContract = z.custom<CustomAttributeData>(
  (value) => customAttributeDataSchema.safeParse(value).success,
  { message: 'Invalid questionnaire custom attribute data' }
);

export const parseQuestionnaireFormSchema = (
  value: unknown,
  fallback: CustomSchema
): CustomSchema =>
  value === null || value === undefined
    ? fallback
    : questionnaireFormSchemaContract.parse(value);

export const parseQuestionnaireFormUiSchema = (
  value: unknown,
  fallback: CustomUISchema
): CustomUISchema =>
  value === null || value === undefined
    ? fallback
    : questionnaireFormUiSchemaContract.parse(value);

export const parseQuestionnaireSchemaForRendering = (
  value: unknown,
  fallback: JsonSchema
): JsonSchema =>
  value === null || value === undefined
    ? fallback
    : questionnaireRenderingSchemaContract.parse(value);

export const parseQuestionnaireUiSchemaForRendering = (
  value: unknown,
  fallback: UISchemaElement
): UISchemaElement =>
  value === null || value === undefined
    ? fallback
    : questionnaireRenderingUiSchemaContract.parse(value);

export const parseQuestionnaireResponseData = (value: unknown): JSONObject =>
  value === null || value === undefined ? {} : jsonObjectSchema.parse(value);

export const parseQuestionnaireCustomAttributeData = (
  value: unknown
): CustomAttributeData | null | undefined =>
  value === null || value === undefined
    ? value
    : questionnaireCustomAttributeDataContract.parse(value);

export const serializeQuestionnaireJsonObject = (value: unknown): JSONObject =>
  jsonObjectSchema.parse(value);
